import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip,
  Collapse,
  Divider,
  Paper,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";

/**
 * Achievements Page (MUI)
 * - White background
 * - Responsive for mobile / tablet / desktop
 * - Accordion style: + to open, - to close
 * - Highlight card styled differently
 *
 * Usage:
 * <AchievementPage />
 */

function AchievementCard({ item, isOpen, onToggle }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: item.highlight ? "warning.light" : "divider",
        bgcolor: item.highlight ? "warning.50" : "#fff",
        overflow: "hidden",
        transition: "box-shadow 200ms ease",
        "&:hover": { boxShadow: 3 },
      }}
    >
      {/* Header */}
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onToggle();
        }}
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
        aria-expanded={isOpen}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.2,
                color: "text.primary",
                wordBreak: "break-word",
              }}
            >
              {item.title}
            </Typography>

            {item.highlight && (
              <Chip
                size="small"
                label="Highlight"
                color="warning"
                variant="filled"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>

          {item.subtitle && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.75,
                color: "text.secondary",
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
              }}
            >
              {item.subtitle}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-label={isOpen ? "Collapse" : "Expand"}
          sx={{
            flexShrink: 0,
            borderRadius: 2,
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 },
            border: "1px solid",
            borderColor: item.highlight ? "warning.light" : "divider",
          }}
        >
          {isOpen ? <RemoveRoundedIcon /> : <AddRoundedIcon />}
        </IconButton>
      </Box>

      <Collapse in={isOpen} timeout={250} unmountOnExit>
        <Divider />
        <CardContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontSize: { xs: "0.9rem", sm: "0.98rem" },
              lineHeight: 1.7,
            }}
          >
            {item.description}
          </Typography>

          {item.bullets?.length ? (
            <Box component="ul" sx={{ mt: 1.5, pl: 2.2, mb: 0 }}>
              {item.bullets.map((b, idx) => (
                <Box component="li" key={idx} sx={{ mt: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: { xs: "0.88rem", sm: "0.95rem" } }}
                  >
                    {b}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </CardContent>
      </Collapse>
    </Card>
  );
}

export default function AchievementPage() {
  const achievements = useMemo(
    () => [
      {
        id: "highlight-1",
        highlight: true,
        title: "We Are Start India — Selected Under Fund Scheme (Corporate Category)",
        subtitle: "Under IIT Dhristi (Founded by IIT Indore)",
        description:
          'Our startup achieved a major milestone by being selected under the “We Are Start India” funding scheme in the Corporate category. This selection came through IIT Dhristi, a startup ecosystem founded by IIT Indore, and it validates the credibility of our vision, the quality of our execution, and our long-term growth potential.',
        bullets: [
          "Selected under a reputed funding scheme",
          "Corporate-category recognition and validation",
          "Supported by the IIT Dhristi startup ecosystem",
        ],
      },
      {
        id: "a2",
        title: "Project Delivery Milestone",
        subtitle: "Delivered on time with quality execution",
        description:
          "Successfully completed a key project milestone within the planned timeline by coordinating tasks, tracking progress, and ensuring strong quality standards across the deliverables.",
        bullets: ["On-time completion", "Quality checks", "Clear coordination"],
      },
      {
        id: "a3",
        title: "Process & Team Improvement",
        subtitle: "Better workflow and team support",
        description:
          "Improved internal workflow by documenting key processes and supporting teammates with guidance, leading to smoother collaboration and faster execution.",
        bullets: ["Process documentation", "Mentoring support", "Improved collaboration"],
      },
    ],
    []
  );

  // Default open highlight
  const [openId, setOpenId] = useState("highlight-1");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 } }}>
        {/* Page Header */}
        <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: -0.4,
              fontSize: { xs: "1.7rem", sm: "2rem", md: "2.2rem" },
            }}
          >
            Achievements
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              color: "text.secondary",
              maxWidth: 820,
              fontSize: { xs: "0.95rem", sm: "1rem" },
            }}
          >
         
          </Typography>
        </Box>

        {/* Responsive Layout: left info (desktop), right list */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
            gap: { xs: 2.5, sm: 3 },
            alignItems: "start",
          }}
        >
          {/* Left Panel */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              p: { xs: 2, sm: 2.5 },
              position: { md: "sticky" },
              top: { md: 24 },
              bgcolor: "#fff",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Overview
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", lineHeight: 1.7 }}>
              These achievements highlight key milestones, recognition, and impact. The top card is
              your main highlight.
            </Typography>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 1.5,
                  bgcolor: "#fff",
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Total Achievements
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                  {/* {achievements.length} */}50+
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "warning.light",
                  p: 1.5,
                  bgcolor: "warning.50",
                }}
              >
                <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 700 }}>
                  Top Highlight
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 800 }}>
                  We Are Start India — Fund Scheme Selection
                </Typography>
              </Paper>

              <Typography variant="caption" sx={{ color: "text.secondary" }}>
            
              </Typography>
            </Stack>
          </Paper>

          {/* Right List */}
          <Box>
            <Stack spacing={2}>
              {achievements.map((item) => (
                <AchievementCard
                  key={item.id}
                  item={item}
                  isOpen={openId === item.id}
                  onToggle={() => setOpenId((prev) => (prev === item.id ? null : item.id))}
                />
              ))}
            </Stack>

          {/*  <Paper
              elevation={0}
              sx={{
                mt: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                p: { xs: 2, sm: 2.5 },
                bgcolor: "#fff",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Responsive View
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2.2, mb: 0, color: "text.secondary" }}>
                <li>
                  <Typography variant="body2">Mobile: single column, compact spacing</Typography>
                </li>
                <li>
                  <Typography variant="body2">Tablet: bigger text and padding</Typography>
                </li>
                <li>
                  <Typography variant="body2">Desktop: two-column layout with sticky overview</Typography>
                </li>
              </Box>
            </Paper>*/}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
